/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { EPR_TYPE, ISiteMngData } from '../types';
import {
  formatSitesForTranslation,
  saveSites,
  translateSites,
  getSites,
  publishSiteDetails,
  IPublishSiteDetailsRequest,
  ISaveSitesOptions,
  Roles,
} from '../helpers';
import TranslateModal from '../TranslateModal';
import AlertModal from '../Modals/AlertModal';
import BulkProgressModal from '../Modals/BulkProgressModal';
import { useCallback, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '../../../logger';

interface IProps {
  data: ISiteMngData[];
  rowSelection: any;
  setRowSelection: any;
  editedUids: string[];
  setEditedUids: React.Dispatch<React.SetStateAction<string[]>>;
  selectedEprName: string;
  setData: (newData: ISiteMngData[]) => void;
  setCloneData: (newData: ISiteMngData[]) => void;
  role: string;
  setIsLoading: any;
  onDataRefetched?: (savedUids: string[]) => void;
}

const SOURCE_SMA = 'SMA';

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const MAX_BATCH_RETRIES = 3;
const RETRY_BACKOFF_MS = process.env.NODE_ENV === 'test' ? 0 : 350;
const FAILURE_CTMS_IDS_MAX_SHOWN = 10;

type BulkPhase = 'save' | 'publish' | 'saveAndPublish';

type BulkProgressStep = 'starting' | 'processing' | 'retrying' | 'batchComplete' | 'batchFailed';

interface IBulkProgress {
  open: boolean;
  title: string;
  status: string;
  detail?: string;
  value: number;
}

/** CTMS row ID for user-facing messages; placeholder when missing. */
function formatCtmsRowIdForProgress(site: ISiteMngData): string {
  const id = site.ctmsRowId?.trim();
  if (id) return String(id);
  return '(no CTMS row ID)';
}

function formatBatchCtmsRowIds(
  batch: ISiteMngData[],
  maxShown = FAILURE_CTMS_IDS_MAX_SHOWN
): string {
  const ids = batch.map(formatCtmsRowIdForProgress);
  if (ids.length <= maxShown) return ids.join(', ');
  return `${ids.slice(0, maxShown).join(', ')} (+${ids.length - maxShown} more)`;
}

function clampBulkProgressPercent(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function buildBulkProgressContent(params: {
  step: BulkProgressStep;
  completed: number;
  grandTotal: number;
  batchIndex?: number;
  batchCount?: number;
  batchSize?: number;
  attempt?: number;
  maxAttempts?: number;
}): Pick<IBulkProgress, 'status' | 'detail'> {
  const { step, completed, grandTotal, batchIndex, batchCount, batchSize, attempt, maxAttempts } =
    params;
  const overall = `${completed} of ${grandTotal} site(s) complete`;
  const multiBatch = batchCount != null && batchCount > 1;

  switch (step) {
    case 'starting':
      return { status: 'Preparing…', detail: overall };
    case 'processing':
      return {
        status: multiBatch ? `Processing batch ${batchIndex} of ${batchCount}` : 'Processing…',
        detail: batchSize ? `${overall} · ${batchSize} site(s) in this batch` : overall,
      };
    case 'retrying':
      return {
        status: `Retrying batch ${batchIndex} (attempt ${attempt} of ${maxAttempts})`,
        detail: overall,
      };
    case 'batchComplete':
      return {
        status: multiBatch ? `Batch ${batchIndex} of ${batchCount} complete` : 'Batch complete',
        detail: overall,
      };
    case 'batchFailed':
      return {
        status: `Batch ${batchIndex} failed`,
        detail: `Continuing with remaining batches · ${overall}`,
      };
    default:
      return { status: overall };
  }
}

function emitBulkProgress(
  onProgress: (p: IBulkProgress) => void,
  partial: Omit<IBulkProgress, 'open'> & { open?: boolean }
) {
  onProgress({ open: true, ...partial });
}

function persistFailureUserGuidance(phase: BulkPhase, failed: ISiteMngData[]): string {
  if (failed.length === 0) return '';
  const ctmsIds = formatBatchCtmsRowIds(failed);
  if (phase === 'publish') {
    return `Could not publish (${failed.length} site(s)): ${ctmsIds}\n\nSelect those staging rows and use Publish to try again.`;
  }
  if (phase === 'saveAndPublish') {
    return `Could not save and publish (${failed.length} site(s)): ${ctmsIds}\n\nRetry Save and Publish for those rows only, or Save then Publish separately.`;
  }
  return `Could not save to staging (${failed.length} site(s)): ${ctmsIds}\n\nUse Save again for those rows only.`;
}

interface IFooterAlertModal {
  open: boolean;
  message: string;
  title?: string;
  primaryButton?: { label: string; onClick: () => void };
  cancelLabel?: string;
}

async function saveSitesInBatchesWithRetry(
  sites: ISiteMngData[],
  smaBatchSize: number,
  phase: BulkPhase,
  onProgress: (p: IBulkProgress) => void
): Promise<{ ok: boolean; completed: number; failed: ISiteMngData[]; protocolItemId: string }> {
  const grandTotal = sites.length;
  if (grandTotal === 0) {
    return { ok: true, completed: 0, failed: [], protocolItemId: '' };
  }
  const phaseTitle =
    phase === 'save'
      ? 'Saving to staging'
      : phase === 'publish'
      ? 'Publishing to Sitecore'
      : 'Save and Publish';
  const saveSitesOptions: ISaveSitesOptions | undefined =
    phase === 'saveAndPublish'
      ? { saveAndPublish: true, returnProtocolItemId: true }
      : phase === 'publish'
      ? { saveAndPublish: false, returnProtocolItemId: true }
      : { saveAndPublish: false };
  const succeededMaster: ISiteMngData[] = [];
  const failed: ISiteMngData[] = [];
  let lastResponseProtocolItemId = '';
  const batchCountInPass = Math.ceil(sites.length / smaBatchSize) || 1;

  emitBulkProgress(onProgress, {
    title: phaseTitle,
    value: 0,
    ...buildBulkProgressContent({ step: 'starting', completed: 0, grandTotal }),
  });

  for (let i = 0; i < sites.length; i += smaBatchSize) {
    const batchIndex = Math.floor(i / smaBatchSize) + 1;
    const batch = sites.slice(i, i + smaBatchSize);
    const progressValue = () =>
      clampBulkProgressPercent((succeededMaster.length / grandTotal) * 100);

    let batchSucceeded = false;
    let attempt = 0;
    while (attempt < MAX_BATCH_RETRIES && !batchSucceeded) {
      attempt += 1;
      if (attempt === 1) {
        emitBulkProgress(onProgress, {
          title: phaseTitle,
          value: progressValue(),
          ...buildBulkProgressContent({
            step: 'processing',
            completed: succeededMaster.length,
            grandTotal,
            batchIndex,
            batchCount: batchCountInPass,
            batchSize: batch.length,
          }),
        });
      }
      const saveResult = ((await saveSites(batch, saveSitesOptions)) ?? '').trim();
      if (phase === 'save') {
        batchSucceeded = saveResult === 'saved';
      } else if (saveResult) {
        batchSucceeded = true;
        lastResponseProtocolItemId = saveResult;
      }
      if (!batchSucceeded && attempt < MAX_BATCH_RETRIES) {
        emitBulkProgress(onProgress, {
          title: phaseTitle,
          value: progressValue(),
          ...buildBulkProgressContent({
            step: 'retrying',
            completed: succeededMaster.length,
            grandTotal,
            batchIndex,
            attempt: attempt + 1,
            maxAttempts: MAX_BATCH_RETRIES,
          }),
        });
        await sleep(RETRY_BACKOFF_MS * attempt);
      }
    }

    if (batchSucceeded) {
      succeededMaster.push(...batch);
      emitBulkProgress(onProgress, {
        title: phaseTitle,
        value: clampBulkProgressPercent((succeededMaster.length / grandTotal) * 100),
        ...buildBulkProgressContent({
          step: 'batchComplete',
          completed: succeededMaster.length,
          grandTotal,
          batchIndex,
          batchCount: batchCountInPass,
        }),
      });
      continue;
    }

    failed.push(...batch);
    emitBulkProgress(onProgress, {
      title: phaseTitle,
      value: progressValue(),
      ...buildBulkProgressContent({
        step: 'batchFailed',
        completed: succeededMaster.length,
        grandTotal,
        batchIndex,
      }),
    });
  }

  return {
    ok: failed.length === 0,
    completed: succeededMaster.length,
    failed,
    protocolItemId: lastResponseProtocolItemId,
  };
}

const FooterActions: React.FC = (props: IProps) => {
  const rowSelection = props.rowSelection;
  const editedUids = props.editedUids;
  const [isTranslateOpen, setIsTranslateOpen] = useState<boolean>(false);
  const selectedEpr = props.selectedEprName || '';
  const selectedFilters = useSelector((state: any) => state?.sma?.selectedFilters);
  const selectedProtocol = useSelector((state: any) => state?.sma?.selectedProtocol);
  const selectedProtocolItemId = useSelector((state: any) => state?.sma?.selectedProtocolItemId);
  const fieldOptions = useSelector((state: any) => state?.sma?.fieldOptions);
  const eprFieldOptions = useSelector((state: any) => state?.sma?.eprFieldOptions);
  const userName = useSelector((state: any) => state?.sma?.userName);
  const SmaBatchSize = parseInt(process.env.SMA_BATCH_SIZE || '5', 10);
  const [alertModal, setAlertModal] = useState<IFooterAlertModal>({
    open: false,
    message: '',
  });

  const closeAlertModal = useCallback(() => {
    setAlertModal({
      open: false,
      message: '',
      title: undefined,
      primaryButton: undefined,
      cancelLabel: undefined,
    });
  }, []);
  const bulkOperationLockRef = useRef(false);
  const [isBulkBusy, setIsBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<IBulkProgress>({
    open: false,
    title: '',
    status: '',
    value: 0,
  });

  const closeBulkProgress = useCallback(() => {
    setBulkProgress((p) => ({ ...p, open: false }));
  }, []);

  const refetchSitesInBackground = useCallback(
    (onFetched: (data: ISiteMngData[]) => void) => {
      if (!selectedProtocol || !selectedFilters?.language) return;
      void getSites(selectedProtocol, selectedFilters.language, selectedFilters.EPR).then(
        onFetched
      );
    },
    [selectedProtocol, selectedFilters]
  );

  /** Merge refetched data with unsaved local edits (O(n) via Map lookup). */
  const mergeRefetchedWithUnsaved = (
    refetchedData: ISiteMngData[],
    unsavedRows: ISiteMngData[]
  ): ISiteMngData[] => {
    const refetchedSmaRowIds = new Set(refetchedData.map((r) => r.smaRowId));
    const unsavedBySmaRowId = new Map(unsavedRows.map((item) => [item.smaRowId, item]));
    const merged = refetchedData.map((refetchedItem) => {
      const unsaved = unsavedBySmaRowId.get(refetchedItem.smaRowId);
      return unsaved ? { ...unsaved, uid: refetchedItem.uid } : refetchedItem;
    });
    const newLocal = unsavedRows.filter((item) => !refetchedSmaRowIds.has(item.smaRowId));
    return [...merged, ...newLocal];
  };

  /**
   * Saves selected rows. If no Rows are selected all edited rows are saved
   */
  const onSaveClick = async (isPublish: boolean) => {
    if (bulkOperationLockRef.current) {
      return;
    }
    if (editedUids.length === 0) {
      setAlertModal({
        open: true,
        message:
          'No sites are selected or edited to save. Edit at least one site or select edited rows, then try again.',
        title: 'Nothing to Save',
      });
      return;
    }

    let uidsToSave: string[] = [];
    const isRowsSelected = Object.keys(rowSelection).length > 0;
    if (isRowsSelected) {
      const selectedRowIds = Object.keys(rowSelection);
      selectedRowIds.forEach((selectedId) => {
        if (editedUids.includes(selectedId)) {
          uidsToSave.push(selectedId);
        }
      });
    } else {
      uidsToSave = editedUids;
    }

    if (uidsToSave.length === 0) {
      setAlertModal({
        open: true,
        message:
          'No matching edited rows to save for the current selection. Select rows that have edits, or clear the selection to save all edited sites.',
        title: 'Nothing to Save',
      });
      return;
    }

    bulkOperationLockRef.current = true;
    setIsBulkBusy(true);
    try {
      const sitesToSave = cloneDeep(props.data.filter((item) => uidsToSave.includes(item.uid)));
      const EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
      let protocolItemId: any = null;
      if (selectedFilters.EPR === 'global') {
        protocolItemId = selectedProtocolItemId;
      }
      const saveLanguage = selectedFilters?.language ?? 'en';
      sitesToSave.forEach((item: any) => {
        item.isPublished = isPublish;
        item.siteDetailTarget = selectedEpr;
        item.language = saveLanguage;
        item.EprId = EprId;
        item.ProtocolItemId = protocolItemId;
        item.radiusInMiles = item?.radiusInMiles || '100';
        item.threshold = item?.threshold || '100';
        item.priority = `${item?.priority || ''}`;
        item.contactEmail = `${item?.contactEmail || ''}`;
        item.studyStaffEmails = `${item?.studyStaffEmails || ''}`;
        item.UpdatedBy = userName;
      });

      const saveBatchResult = await saveSitesInBatchesWithRetry(
        sitesToSave,
        SmaBatchSize,
        'save',
        setBulkProgress
      );
      const failedUidSet = new Set(saveBatchResult.failed.map((s) => s.uid));
      const uidsToSaveSetForOp = new Set(uidsToSave);

      props.setEditedUids((prev) =>
        prev.filter((uid) => !uidsToSaveSetForOp.has(uid) || failedUidSet.has(uid))
      );

      closeBulkProgress();

      if (saveBatchResult.ok) {
        setAlertModal({
          open: true,
          title: 'Save Complete',
          message: `${saveBatchResult.completed} site(s) saved to staging.`,
        });
      } else if (saveBatchResult.completed > 0) {
        logger.error('Partial save to staging', {
          respose: `${saveBatchResult.completed} of ${sitesToSave.length} sites saved`,
          payload: {},
        });
        setAlertModal({
          open: true,
          title: 'Save Partially Complete',
          message: `${saveBatchResult.completed} of ${
            sitesToSave.length
          } site(s) saved to staging.\n\n${persistFailureUserGuidance(
            'save',
            saveBatchResult.failed
          )}`,
        });
      } else {
        logger.error('Unable to process sites to staging', {
          respose: `0 of ${sitesToSave.length} sites saved`,
          payload: {},
        });
        setAlertModal({
          open: true,
          title: 'Save Failed',
          message: `No sites could be saved to staging.\n\n${persistFailureUserGuidance(
            'save',
            saveBatchResult.failed
          )}`,
        });
      }

      if (saveBatchResult.completed > 0) {
        refetchSitesInBackground((refetchedData) => {
          props.setData(refetchedData);
          props.setCloneData(refetchedData);
          props.setRowSelection({});
        });
      }
    } catch (_error) {
      closeBulkProgress();
      setAlertModal({
        open: true,
        title: 'Save Error',
        message: 'An unexpected error occurred while saving. Please try again.',
      });
    } finally {
      bulkOperationLockRef.current = false;
      setIsBulkBusy(false);
      setBulkProgress((p) => ({ ...p, open: false }));
    }
  };

  const onSaveAndPublishClick = async () => {
    if (bulkOperationLockRef.current) {
      return;
    }
    if (editedUids.length === 0) {
      setAlertModal({
        open: true,
        message:
          'Unable to save and publish: No sites have been edited. Please edit at least one site or make changes to existing sites before attempting to save and publish.',
        title: 'No Sites to Save and Publish',
      });
      return;
    }

    const editedSet = new Set(editedUids);
    const selectedRowIds = Object.keys(rowSelection);
    const isRowsSelected = selectedRowIds.length > 0;

    let uidsToSave: string[];
    let nonEditedSelectedRows: string[] = [];

    if (isRowsSelected) {
      nonEditedSelectedRows = selectedRowIds.filter((id) => !editedSet.has(id));
      uidsToSave = selectedRowIds.filter((id) => editedSet.has(id));
      if (uidsToSave.length === 0) {
        setAlertModal({
          open: true,
          message:
            'Validation error: You are trying to Save and Publish a row which is not edited. Please ensure that all selected rows have been edited before saving and publishing, or deselect the rows that have not been modified.',
          title: 'Cannot Save and Publish Unedited Rows',
        });
        return;
      }
    } else {
      uidsToSave = editedUids;
    }

    const uidsToSaveSet = new Set(uidsToSave);
    const sitesToSaveAndPublish = props.data.filter((item) => uidsToSaveSet.has(item.uid));

    bulkOperationLockRef.current = true;
    setIsBulkBusy(true);
    try {
      const rowsWithPriorityZero = sitesToSaveAndPublish.filter((item) => {
        const p = item?.priority as unknown;
        return p === 0 || p === '0' || (typeof p === 'string' && String(p).trim() === '0');
      });
      if (rowsWithPriorityZero.length > 0) {
        const siteNames = rowsWithPriorityZero.map((item) => item?.siteName).join(', ');
        setAlertModal({
          open: true,
          message: `Priority validation failed: The following site(s) have a Priority value of 0, which is not allowed:\n${siteNames}\n\nPlease update the Priority values for these sites to a valid number (1-9) before proceeding with Save and Publish.`,
          title: 'Priority Validation Failed',
        });
        return;
      }

      const sitesToSave = cloneDeep(sitesToSaveAndPublish);
      const EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
      let protocolItemId: any = null;
      if (selectedFilters.EPR === 'global') {
        protocolItemId = selectedProtocolItemId;
      }
      const saveAndPublishLanguage = selectedFilters?.language ?? 'en';
      sitesToSave.forEach((item: any) => {
        item.isPublished = true;
        item.siteDetailTarget = selectedEpr;
        item.language = saveAndPublishLanguage;
        item.EprId = EprId;
        item.ProtocolItemId = protocolItemId;
        item.radiusInMiles = item?.radiusInMiles || '100';
        item.threshold = item?.threshold || '100';
        item.priority = `${item?.priority || ''}`;
        item.contactEmail = `${item?.contactEmail || ''}`;
        item.studyStaffEmails = `${item?.studyStaffEmails || ''}`;
        item.UpdatedBy = userName;
      });

      const currentUnsavedEditedRows = props.data.filter(
        (item) => editedSet.has(item.uid) && !uidsToSaveSet.has(item.uid)
      );

      const combinedResult = await saveSitesInBatchesWithRetry(
        sitesToSave,
        SmaBatchSize,
        'saveAndPublish',
        setBulkProgress
      );

      const failedUidSet = new Set(combinedResult.failed.map((s) => s.uid));

      const completedCount = combinedResult.completed;

      props.setEditedUids((prev) =>
        prev.filter((uid) => !uidsToSaveSet.has(uid) || failedUidSet.has(uid))
      );

      const publishSiteDetailsPayload: IPublishSiteDetailsRequest = {
        Language: selectedFilters.language ?? null,
        EprId: EprId,
        ProtocolItemId: combinedResult.protocolItemId || null,
      };
      publishSiteDetails(publishSiteDetailsPayload);

      props.setRowSelection({});

      const noteSkipped = nonEditedSelectedRows.length
        ? ` Note: ${nonEditedSelectedRows.length} selected site(s) were skipped because they had no edits.`
        : '';

      closeBulkProgress();

      if (combinedResult.completed === 0) {
        setAlertModal({
          open: true,
          message: `No sites could be saved and published.\n\n${persistFailureUserGuidance(
            'saveAndPublish',
            combinedResult.failed
          )}`,
          title: 'Save and Publish Failed',
        });
        return;
      }

      if (combinedResult.ok) {
        setAlertModal({
          open: true,
          message: `Success: ${completedCount} site(s) saved and published to Sitecore. Changes should be visible on the website.${noteSkipped}`,
          title: 'Save and Publish Successful',
        });
      } else {
        setAlertModal({
          open: true,
          title: 'Save and Publish incomplete',
          message: [
            `Saved and published: ${completedCount} of ${sitesToSave.length} site(s).${noteSkipped}`,
            persistFailureUserGuidance('saveAndPublish', combinedResult.failed),
          ].join('\n\n'),
        });
      }

      if (combinedResult.completed > 0) {
        refetchSitesInBackground((refetchedDataAfterPublish) => {
          const finalDataAfterPublish = mergeRefetchedWithUnsaved(
            refetchedDataAfterPublish,
            currentUnsavedEditedRows
          );
          props.setData(finalDataAfterPublish);
          props.setCloneData(finalDataAfterPublish);
          if (props.onDataRefetched) {
            props.onDataRefetched(uidsToSave);
          }
        });
      }
    } catch (_error) {
      closeBulkProgress();
      setAlertModal({
        open: true,
        message:
          'An unexpected error occurred while attempting to save and publish the sites. Please try again, or contact the Support team if the issue continues.',
        title: 'Error',
      });
    } finally {
      bulkOperationLockRef.current = false;
      setIsBulkBusy(false);
      setBulkProgress((p) => ({ ...p, open: false }));
    }
  };

  const runPublishWithApprovedRows = async (approvedRows: ISiteMngData[]) => {
    if (bulkOperationLockRef.current) {
      return;
    }
    bulkOperationLockRef.current = true;
    setIsBulkBusy(true);
    try {
      const rows = cloneDeep(approvedRows);
      const EprId = selectedFilters.EPR === 'global' ? null : selectedFilters.EPR;
      let protocolItemId: any = null;
      if (selectedFilters.EPR === 'global') {
        protocolItemId = selectedProtocolItemId;
      }
      const publishLanguage = selectedFilters?.language ?? 'en';
      rows.forEach((item: any) => {
        item.isPublished = true;
        item.siteDetailTarget = selectedEpr;
        item.language = publishLanguage;
        item.EprId = EprId;
        item.radiusInMiles = item?.radiusInMiles || '100';
        item.threshold = item?.threshold || '100';
        item.priority = `${item?.priority || ''}`;
        item.contactEmail = item?.contactEmail || '';
        item.studyStaffEmails = item?.studyStaffEmails || '';
        item.ProtocolItemId = protocolItemId;
        item.UpdatedBy = userName;
      });
      const publishBatchResult = await saveSitesInBatchesWithRetry(
        rows,
        SmaBatchSize,
        'publish',
        setBulkProgress
      );

      const publishSiteDetailsPayload: IPublishSiteDetailsRequest = {
        Language: selectedFilters.language ?? null,
        EprId: EprId,
        ProtocolItemId: publishBatchResult.protocolItemId || null,
      };
      publishSiteDetails(publishSiteDetailsPayload);

      props.setRowSelection({});

      closeBulkProgress();

      if (publishBatchResult.ok) {
        setAlertModal({
          open: true,
          title: 'Publish Complete',
          message: `${publishBatchResult.completed} site(s) published to Sitecore.`,
        });
      } else if (publishBatchResult.completed > 0) {
        logger.error('Partial publish to Sitecore', {
          respose: `${publishBatchResult.completed} of ${approvedRows.length} sites published`,
          payload: {},
        });
        setAlertModal({
          open: true,
          title: 'Publish Partially Complete',
          message: `${publishBatchResult.completed} of ${
            approvedRows.length
          } site(s) published to Sitecore.\n\n${persistFailureUserGuidance(
            'publish',
            publishBatchResult.failed
          )}`,
        });
      } else {
        logger.error('Unable to process sites to publishing.', {
          respose: `0 of ${approvedRows.length} sites published`,
          payload: {},
        });
        setAlertModal({
          open: true,
          title: 'Publish Failed',
          message: `No sites could be published.\n\n${persistFailureUserGuidance(
            'publish',
            publishBatchResult.failed
          )}`,
        });
      }

      if (publishBatchResult.completed > 0) {
        refetchSitesInBackground((refetchedData) => {
          props.setCloneData(refetchedData);
          props.setData(refetchedData);
        });
      }
    } catch (_error) {
      closeBulkProgress();
      setAlertModal({
        open: true,
        title: 'Publish Error',
        message: 'Error publishing sites. Please try again or contact the Support team.',
      });
    } finally {
      bulkOperationLockRef.current = false;
      setIsBulkBusy(false);
      setBulkProgress((p) => ({ ...p, open: false }));
    }
  };

  const onPublish = () => {
    try {
      const selectedRowIds = Object.keys(rowSelection);
      if (selectedRowIds.length < 1) {
        setAlertModal({
          open: true,
          title: 'No Selection',
          message: 'No sites are selected. Please select the sites to be published.',
        });
        return;
      }
      if (!Array.isArray(props.data)) {
        setAlertModal({
          open: true,
          title: 'Error',
          message: 'Please contact the Support team for further assistance.',
        });
        return;
      }
      const approvedRows = props.data.filter((item) => {
        // @ts-ignore
        return item.source === 'staging' && selectedRowIds.includes(item.uid);
      });
      const rowsWithPriorityZero = approvedRows?.filter((item) => item?.priority === 0);
      if (rowsWithPriorityZero?.length > 0) {
        const siteNames = rowsWithPriorityZero.map((item) => item?.siteName).join(', ');
        setAlertModal({
          open: true,
          title: 'Invalid Priority',
          message: `The following sites have a Priority value of 0:\n${siteNames}\n\nPlease update their Priority values to proceed.`,
        });
        return;
      }
      if (approvedRows.length === 0) {
        setAlertModal({
          open: true,
          title: 'Cannot Publish',
          message: 'Only the sites under Staging can be published.',
        });
        return;
      }
      setAlertModal({
        open: true,
        title: 'Confirm Publish',
        message: `Publishing ${approvedRows.length} site(s) to Sitecore. Changes will be visible on the website. Proceed?`,
        primaryButton: {
          label: 'Proceed',
          onClick: () => {
            closeAlertModal();
            void runPublishWithApprovedRows(approvedRows);
          },
        },
        cancelLabel: 'Cancel',
      });
    } catch (_error) {
      setAlertModal({
        open: true,
        title: 'Publish Error',
        message: 'Error publishing sites. Please try again.',
      });
    }
  };

  /**
   * Translate selected rows.
   */
  const onTranslateClick = async () => {
    const lang = selectedFilters.language;
    try {
      let uidsToFilter: string[] = [];
      const isRowsSelected = Object.keys(rowSelection).length > 0;
      if (isRowsSelected) {
        uidsToFilter = Object.keys(rowSelection);
      }
      if (uidsToFilter.length > 0) {
        const sitesToTranslate = props.data.filter((item) => uidsToFilter.includes(item.uid));
        const fieldOptionsToCheck =
          selectedFilters.EPR === 'global' ? fieldOptions : eprFieldOptions;
        const formatedSites = formatSitesForTranslation(
          sitesToTranslate,
          lang,
          fieldOptionsToCheck
        );
        props.setIsLoading(true);
        const translateSitesOperation = await translateSites(formatedSites);
        if (translateSitesOperation.sitesDataRequest) {
          props.setRowSelection({});
          const clonedTranslatedRows: ISiteMngData[] = [];
          props.data.forEach((item) => {
            translateSitesOperation.sitesDataRequest.forEach((resObj: any) => {
              if (item.uid === resObj.id) {
                const newSmaRowId = uuidv4();
                const newUid = `${newSmaRowId}_${SOURCE_SMA}`;
                const clonedItem = cloneDeep(item);
                const newItem = { ...clonedItem, ...resObj };
                delete newItem.id;
                newItem.subRows = [];
                newItem.smaRowId = newSmaRowId;
                newItem.source = SOURCE_SMA;
                newItem.uid = newUid;
                newItem.language = selectedFilters.language;
                clonedTranslatedRows.push(newItem);
                props.setEditedUids((arr) => [...arr, newUid]);
              }
            });
          });
          props.setData([...clonedTranslatedRows, ...props.data]);
          props.setIsLoading(false);
        }
        props.setIsLoading(false);
      } else {
        props.setIsLoading(false);
        setAlertModal({
          open: true,
          title: 'Nothing to Translate',
          message: 'No sites selected to translate.',
        });
      }
    } catch (_error) {
      props.setIsLoading(false);
      setAlertModal({
        open: true,
        title: 'Translate Error',
        message: 'An error occurred while translating. Please try again.',
      });
    }
  };
  const isBtnDisabled = Array.isArray(props.data) && props.data.length < 1;

  return (
    <>
      <TranslateModal
        isOpen={isTranslateOpen}
        setOpen={setIsTranslateOpen}
        handleTranslateClick={onTranslateClick}
      />
      <AlertModal
        open={alertModal.open}
        message={alertModal.message}
        title={alertModal.title}
        primaryButton={alertModal.primaryButton}
        cancelLabel={alertModal.cancelLabel}
        onClose={closeAlertModal}
      />
      <BulkProgressModal
        open={bulkProgress.open}
        title={bulkProgress.title}
        status={bulkProgress.status}
        detail={bulkProgress.detail}
        value={bulkProgress.value}
      />
      <Box width="50%">
        <Stack spacing={3} direction="row">
          <Button
            variant="contained"
            disabled={isBtnDisabled || isBulkBusy}
            fullWidth
            onClick={() => onSaveClick(false)}
          >
            Save
          </Button>
          {props.role &&
            (props.role.includes(Roles.ADMIN) || props.role.includes(Roles.APPROVER)) && (
              <Button
                variant="contained"
                disabled={isBtnDisabled || isBulkBusy}
                fullWidth
                onClick={() => onPublish()}
              >
                Publish
              </Button>
            )}
          {props.role &&
            (props.role.includes(Roles.ADMIN) || props.role.includes(Roles.APPROVER)) && (
              <Button
                variant="contained"
                disabled={isBtnDisabled || isBulkBusy}
                fullWidth
                onClick={() => onSaveAndPublishClick()}
              >
                Save and Publish
              </Button>
            )}
          {selectedFilters.language !== 'en' && selectedFilters.EPR === EPR_TYPE.Global && (
            <Button
              variant="contained"
              disabled={isBtnDisabled || isBulkBusy}
              fullWidth
              onClick={() => setIsTranslateOpen(true)}
            >
              Translate
            </Button>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default FooterActions;
